import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './button';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { X, User, Search, AlertTriangle } from 'lucide-react';
import { getProfiles } from '../../lib/ProfileDataService';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';

const LAST_SELECTED_PROFILE_KEY = 'lastSelectedProfile';

const ProfileSelectionModal = ({ 
  isOpen, 
  onClose, 
  onNext,
  selectedResult,
  resultTitle,
  resultUrl,
  disabled = false 
}) => {
  const [profiles, setProfiles] = useState([]);
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNoProfilesModal, setShowNoProfilesModal] = useState(false);
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      fetchProfiles();
      setSearchTerm('');
    }
  }, [isOpen]);

  // Restore last selected profile after profiles are loaded
  useEffect(() => {
    if (profiles.length > 0 && !selectedProfile) {
      try {
        const savedProfileId = localStorage.getItem(LAST_SELECTED_PROFILE_KEY);
        if (savedProfileId) {
          const savedProfile = profiles.find(profile => profile.id === savedProfileId);
          if (savedProfile) {
            setSelectedProfile(savedProfile);
            // Auto-proceed to next step if profile was restored from localStorage
            setTimeout(() => {
              if (onNext && savedProfile) {
                onNext(savedProfile);
              }
            }, 100); // Small delay to ensure state is updated
          }
        }
      } catch (error) {
        console.warn('Failed to load saved profile from localStorage:', error);
      }
    }
  }, [profiles, selectedProfile, onNext]);

  useEffect(() => {
    // Filter profiles based on search term
    if (searchTerm.trim()) {
      const filtered = profiles.filter(profile =>
        profile.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        profile.searchType.toString().includes(searchTerm.toLowerCase())
      );
      setFilteredProfiles(filtered);
    } else {
      setFilteredProfiles(profiles);
    }
  }, [profiles, searchTerm]);

  // Auto-select profile if there's only one option
  useEffect(() => {
    if (filteredProfiles.length === 1 && !selectedProfile) {
      setSelectedProfile(filteredProfiles[0]);
    }
  }, [filteredProfiles, selectedProfile]);

  // Check for no profiles and show modal
  useEffect(() => {
    if (!loading && profiles.length === 0 && !error) {
      setShowNoProfilesModal(true);
    }
  }, [loading, profiles.length, error]);

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getProfiles(1); // Get active profiles only
      setProfiles(data.profiles || []);
    } catch (err) {
      setError('Failed to fetch profiles');
      console.error('Error fetching profiles:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (selectedProfile) {
      // Pass the selected profile to parent and let parent manage both modals
      onNext && onNext(selectedProfile);
    }
  };

  const handleClose = () => {
    setSelectedProfile(null);
    setSearchTerm('');
    onClose();
  };

  const handleNoProfilesOk = () => {
    setShowNoProfilesModal(false);
    onClose();
    navigate('/profiles/edit/new');
  };

  if (!isOpen) return null;

  // Show no profiles modal
  if (showNoProfilesModal) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/50 transition-opacity" />
        
        {/* No Profiles Modal */}
        <Card className="relative w-full max-w-md mx-4 shadow-lg">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              {t('profileRequired')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t('noProfilesSetupRequired')}
            </p>
            <div className="flex justify-end">
              <Button onClick={handleNoProfilesOk} className="w-full sm:w-auto">
                {t('ok')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <Card className="relative w-full max-w-2xl mx-4 shadow-lg max-h-[80vh] flex flex-col">
        <CardHeader className="pb-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="h-5 w-5 text-primary" />
              {t('selectProfile')}
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="h-6 w-6 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          {/* Context Info */}
          <div className="mt-4 p-3 bg-muted rounded-lg">
            <p className="text-sm font-medium text-foreground mb-1">
              {t('submittingResult')}:
            </p>
            <p className="text-sm text-muted-foreground font-medium break-words">
              {resultTitle}
            </p>
            <a 
              href={resultUrl} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-xs text-primary hover:underline break-all mt-1 block cursor-pointer"
              title={resultUrl}
            >
              {resultUrl}
            </a>
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-hidden flex flex-col">
          {/* Search Box */}
          <div className="relative mb-4 flex-shrink-0">
            <Search className={`absolute top-3 h-4 w-4 text-muted-foreground ${isRTL ? 'right-3' : 'left-3'}`} />
            <input
              type="text"
              placeholder={t('searchProfiles')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              dir={isRTL ? 'rtl' : 'ltr'}
              className={`w-full py-2 border border-input rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${isRTL ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4 text-left'}`}
            />
          </div>

          {/* Profiles List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-muted-foreground">{t('loading')}</div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-red-500">{error}</div>
              </div>
            ) : filteredProfiles.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-muted-foreground">
                  {searchTerm ? t('noProfilesFoundInSearch') : t('noProfilesAvailable')}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredProfiles.map((profile) => (
                  <div
                    key={profile.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedProfile?.id === profile.id
                        ? 'border-primary bg-primary/5'
                        : 'border-muted hover:border-primary/50 hover:bg-primary/5'
                    }`}
                    onClick={() => setSelectedProfile(profile)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full border-2 ${
                        selectedProfile?.id === profile.id
                          ? 'border-primary bg-primary'
                          : 'border-muted-foreground'
                      }`} />
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-foreground">
                          {profile.name}
                        </h4>
                        <p className="text-xs text-muted-foreground">
                          {t('searchType')}: {profile.searchType === 1 ? t('business') : profile.searchType === 2 ? t('restaurant') : t('unknown')}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-4 pt-4 border-t flex-shrink-0">
            <Button
              variant="outline"
              onClick={handleClose}
              className="w-full sm:w-auto"
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handleNext}
              disabled={disabled || !selectedProfile}
              className="w-full sm:w-auto"
            >
              {t('next') || 'Next'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfileSelectionModal;
